import type { FunctionComponent, KeyboardEvent, MouseEvent, Ref } from 'react'
import React, { useMemo } from 'react'
import { createPath } from 'history'
import isNil from 'lodash/isNil'

import { omit } from '@app/utils/omit'
import { useAppDispatch } from '@app/utils/redux'

import type { LinkProps as RLinkProps } from '@app/packages/routing/Link'
import { Link as ReactLink } from '@app/packages/routing/Link'
import { useRouter } from '@app/packages/routing/RouterContext'
import type { LocationState } from '@app/packages/routing/types'
import { createLocationFromString } from '@app/packages/routing/utils'
import { useThunkSelector } from '@app/packages/thunkSelector/useThunkSelector'

import { useEvent } from '@app/hooks/useEvent'

import { getAnalyticsRouter } from '@app/store/actions/analytics'
import { profileRegionSlugResultSelector } from '@app/store/slices/profile_place'

import { useIsBrowserRender } from '@app/components/BrowserRenderContext/BrowserRenderContext'

import type { LinkEvent } from './events'

export type LinkRef = HTMLAnchorElement

export type LinkProps = Omit<RLinkProps, 'to' | 'state'> & {
  /**
   * Both events this link can send, declared together by whoever renders it — `linkEvent('…')`.
   * Its action is its `to`, so there is nothing to pair a handler with; what varies is how the
   * reader followed it.
   */
  event: LinkEvent
  to?: RLinkProps['to']
  /** whether link should be prefixed with current region */
  region?: boolean
  state?: LocationState
  ref?: Ref<LinkRef>
}

/**
 * AppLink is a wrapper for `react-router.Link`
 * that supports regional link prefixing
 */
export const Link: FunctionComponent<LinkProps> = ({ event, to, onClick, onKeyDown, region = false, children, ref, ...rprops }) => {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const isBrowser = useIsBrowserRender()
  const currentRegion = useThunkSelector(profileRegionSlugResultSelector).suspend()

  const external = typeof to === 'string' && !to.startsWith('/')
  const location = useMemo(() => {
    const loc = typeof to === 'string' ? createLocationFromString(to) : to
    if (isNil(loc)) return loc
    if (region && !currentRegion) {
      loc.pathname = `/${currentRegion}/${loc.pathname}`.replace(/\/{2,}/, '/').replace(/\/$/, '')
    }
    return loc
  }, [currentRegion, region, to])

  const sendEvent = useEvent((via: 'click' | 'keydown') => {
    const destination = { pathname: location?.pathname ?? null, search: location?.search ?? null, hash: location?.hash ?? null }

    dispatch(getAnalyticsRouter()).send(event[via].withData(destination))
  })

  const handleClick = useEvent((e: MouseEvent<HTMLAnchorElement>) => {
    sendEvent('click')
    onClick?.(e)
  })

  const handleKeyDown = useEvent((e: KeyboardEvent<HTMLAnchorElement>) => {
    sendEvent('keydown')
    onKeyDown?.(e)
  })

  if (isNil(location))
    return (
      <a onClick={handleClick} onKeyDown={handleKeyDown} {...rprops} ref={ref}>
        {children}
      </a>
    )

  if (!isBrowser || external)
    return (
      <a href={createPath(location)} onClick={handleClick} onKeyDown={handleKeyDown} {...omit(rprops, 'replace')} ref={ref}>
        {children}
      </a>
    )

  return (
    <ReactLink onClick={handleClick} onKeyDown={handleKeyDown} replace={location.pathname === router.location.pathname} to={location} {...rprops} ref={ref}>
      {children}
    </ReactLink>
  )
}
